import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  MarkerType,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  MiniMap,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./ReportLineage.css";
import "../DataContract/ViewLineage.css";
import PowerBIIcon from "../../../assests/power-bi-icon.svg";

const toSafeNodeId = (value) =>
  String(value || "")
    .replace(/[^a-zA-Z0-9_.-]/g, "-")
    .slice(0, 80);

const extractColumnsData = (columns) => {
  if (!columns || !Array.isArray(columns) || columns.length === 0) {
    return { visibleColumns: [], allColumns: [], showMoreText: null };
  }

  const allColumns = columns.map((col) => {
    if (typeof col === "string") {
      return { name: col, type: "string" };
    }

    return {
      name: col?.name || "",
      type: col?.type_name || col?.type_text || "unknown",
    };
  });

  const visibleColumns = allColumns.slice(0, 3);
  const remainingCount = allColumns.length - 3;

  return {
    visibleColumns,
    allColumns,
    showMoreText: remainingCount > 0 ? `Show ${remainingCount} more columns` : null,
  };
};

// Custom Report Node Component (styled like primary node)
const ReportNode = ({ data, selected }) => {
  return (
    <div
      className={`report-node ${selected ? "report-node-selected" : ""}`}
      title={data.label}
    >
      {/* Report Header */}
      <div className="report-header">
        <div className="report-icon">
          <img src={PowerBIIcon} alt="Power BI" className="report-icon-svg" />
        </div>
        <div className="report-info">
          <div className="report-label">Report</div>
          <div className="report-name">{data.label}</div>
        </div>
      </div>

      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="header-left"
        className="report-handle"
        style={{
          top: "35px",
          left: "-10px",
          transform: "translateY(-50%)",
          position: "absolute",
          zIndex: 10,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="header-right"
        className="report-handle"
        style={{
          top: "35px",
          right: "-10px",
          transform: "translateY(-50%)",
          position: "absolute",
          zIndex: 10,
        }}
      />
    </div>
  );
};

const LineageTableNode = ({ data, selected }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayColumns = isExpanded ? data.allColumns : data.visibleColumns;
  const expandLabel = data.expanded ? "Expanded" : "Expand";

  const handleExpandClick = (event) => {
    event.stopPropagation();
    if (data.onExpandNode && !data.isLoading) {
      data.onExpandNode();
    }
  };

  return (
    <div
      className={`table-node ${selected ? "table-node-selected" : ""}`}
      title={data.tableName || data.label}
    >
      <div className="table-header">
        <button
          type="button"
          className={`report-lineage-expand-pill ${data.expanded ? "is-expanded" : ""} ${
            data.isLoading ? "is-loading" : ""
          }`}
          onClick={handleExpandClick}
          title={data.isLoading ? "Loading..." : "Expand lineage"}
          disabled={data.isLoading}
        >
          {data.isLoading ? (
            <div className="expand-spinner"></div>
          ) : (
            <svg className="report-lineage-expand-icon" fill="none" viewBox="0 0 20 20">
              <path
                d="M17 10H9m0 0l3-3m-3 3l3 3M8 4H4v4M8 16H4v-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          <span className="report-lineage-expand-text">{data.isLoading ? "Loading" : expandLabel}</span>
        </button>

        <div className="table-info">
          <div className="table-label">Table</div>
          <div className="table-name" title={data.tableName || data.label}>
            {data.tableName || data.label}
          </div>
          {data.isLoading && <div className="table-description">Loading lineage...</div>}
          {!data.isLoading && !data.expanded && (
            <div className="table-description">Click to expand lineage</div>
          )}
          {data.expandError && <div className="table-description">{data.expandError}</div>}
        </div>
      </div>

      <div className="table-columns">
        {displayColumns.map((column, index) => (
          <div key={`${column.name}-${index}`} className="column-row" style={{ position: "relative" }}>
            <span className="column-name">{column.name}</span>
            <span className="column-type">{column.type}</span>
          </div>
        ))}

        {data.showMoreText && (
          <div className="show-more-container">
            <button
              className="show-more-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded((prev) => !prev);
              }}
            >
              {isExpanded ? "Hide columns" : data.showMoreText}
            </button>
          </div>
        )}
      </div>

      <Handle
        type="target"
        position={Position.Left}
        id="header-left"
        className="connection-handle header-handle"
        style={{
          top: "35px",
          left: "-10px",
          transform: "translateY(-50%)",
          position: "absolute",
          zIndex: 10,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="header-right"
        className="connection-handle header-handle"
        style={{
          top: "35px",
          right: "-10px",
          transform: "translateY(-50%)",
          position: "absolute",
          zIndex: 10,
        }}
      />
    </div>
  );
};

const nodeTypes = {
  reportNode: ReportNode,
  tableNode: LineageTableNode,
};

const ReportLIneage = ({ reportName, sourceType = "uc", onTableClick }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lineageData, setLineageData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [expandingNodes, setExpandingNodes] = useState({});
  const [expandErrors, setExpandErrors] = useState({});
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  useEffect(() => {
    if (!reportName) {
      setLineageData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadReportLineage = async () => {
      setLoading(true);
      setError(null);
      setSelectedNode(null);
      setExpandedNodes({});
      setExpandingNodes({});
      setExpandErrors({});
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/report-lineage?report_name=${encodeURIComponent(reportName)}&source_type=${sourceType}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (!cancelled) {
          setLineageData(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load report lineage");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadReportLineage();
    return () => {
      cancelled = true;
    };
  }, [reportName, sourceType]);

  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    if (!lineageData) {
      return { nodes: [], edges: [] };
    }

    const reportNode = {
      id: "report-node",
      type: "reportNode",
      position: { x: 600, y: 300 },
      data: { label: lineageData.name || reportName },
    };

    const upstreams = Array.isArray(lineageData.upstreams) ? lineageData.upstreams : [];
    const upstreamNodes = upstreams.map((item, index) => ({
      id: `up-${index}`,
      type: "tableNode",
      position: {
        x: 120,
        y: 160 + index * 140,
      },
      data: (() => {
        const tableName = item.child || `Source ${index + 1}`;
        const { visibleColumns, allColumns, showMoreText } = extractColumnsData(item.columns);
        return {
          label: tableName,
          tableName,
          sourceFullName: tableName,
          visibleColumns,
          allColumns,
          showMoreText,
        };
      })(),
    }));

    const upstreamEdges = upstreams.map((_, index) => ({
      id: `edge-up-${index}`,
      source: `up-${index}`,
      target: "report-node",
      sourceHandle: "header-right",
      targetHandle: "header-left",
      type: "bezier",
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: "#374151",
      },
      style: {
        stroke: "#374151",
        strokeWidth: 2,
      },
    }));

    return {
      nodes: [reportNode, ...upstreamNodes],
      edges: upstreamEdges,
    };
  }, [lineageData, reportName]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes and edges when initial data changes
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const expandTableLineage = useCallback(
    async (nodeId, sourceFullName) => {
      if (!sourceFullName || expandingNodes[nodeId] || expandedNodes[nodeId]) {
        return;
      }

      setExpandingNodes((prev) => ({ ...prev, [nodeId]: true }));
      setExpandErrors((prev) => {
        const next = { ...prev };
        delete next[nodeId];
        return next;
      });

      try {
        const endpoint = `${process.env.REACT_APP_API_URL}/lineage?table_name=${encodeURIComponent(sourceFullName)}&source_type=${encodeURIComponent(sourceType)}`;
        const response = await fetch(endpoint);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const currentNode = nodes.find((n) => n.id === nodeId);
        if (!currentNode) {
          return;
        }

        const tableToNodeId = new Map();
        nodes.forEach((n) => {
          const tableName = n.data?.tableName || n.data?.sourceFullName || n.data?.label;
          if (tableName) {
            tableToNodeId.set(tableName, n.id);
          }
        });

        const nextNodes = [];
        const nextEdges = [];
        const existingEdgeIds = new Set(edges.map((edge) => edge.id));

        const getOrCreateNode = (tableName, position, columns = []) => {
          if (tableToNodeId.has(tableName)) {
            return tableToNodeId.get(tableName);
          }

          const newNodeId = `tbl-${toSafeNodeId(tableName)}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
          tableToNodeId.set(tableName, newNodeId);
          nextNodes.push({
            id: newNodeId,
            type: "tableNode",
            position,
            data: (() => {
              const { visibleColumns, allColumns, showMoreText } = extractColumnsData(columns);
              return {
                label: tableName,
                tableName,
                sourceFullName: tableName,
                visibleColumns,
                allColumns,
                showMoreText,
              };
            })(),
          });

          return newNodeId;
        };

        const upstreams = Array.isArray(data.upstreams) ? data.upstreams : [];
        upstreams.forEach((item, index) => {
          const upstreamTable = item?.child;
          if (!upstreamTable) {
            return;
          }

          const upstreamNodeId = getOrCreateNode(upstreamTable, {
            x: currentNode.position.x - 480,
            y: currentNode.position.y + (index - (upstreams.length - 1) / 2) * 150,
          }, item?.columns);

          const edgeId = `edge-${upstreamNodeId}-${nodeId}`;
          if (!existingEdgeIds.has(edgeId)) {
            existingEdgeIds.add(edgeId);
            nextEdges.push({
              id: edgeId,
              source: upstreamNodeId,
              target: nodeId,
              sourceHandle: "header-right",
              targetHandle: "header-left",
              type: "bezier",
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: "#374151",
              },
              style: {
                stroke: "#374151",
                strokeWidth: 2,
              },
            });
          }
        });

        // Downstreams are fetched but not rendered — upstream-only view for Report Lineage
        // const downstreams = Array.isArray(data.downstreams) ? data.downstreams : [];

        // Update the clicked source node itself with columns from the API response
        const apiColumns = Array.isArray(data.columns) ? data.columns : [];
        if (apiColumns.length > 0) {
          const { visibleColumns, allColumns, showMoreText } = extractColumnsData(apiColumns);
          setNodes((currentNodes) =>
            currentNodes.map((n) =>
              n.id === nodeId
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      visibleColumns,
                      allColumns,
                      showMoreText,
                    },
                  }
                : n
            )
          );
        }

        if (nextNodes.length) {
          setNodes((currentNodes) => [...currentNodes, ...nextNodes]);
        }
        if (nextEdges.length) {
          setEdges((currentEdges) => [...currentEdges, ...nextEdges]);
        }

        if (!nextNodes.length && !nextEdges.length) {
          setExpandErrors((prev) => ({
            ...prev,
            [nodeId]: "No additional lineage found",
          }));
        }

        if (reactFlowInstance && (nextNodes.length || nextEdges.length)) {
          setTimeout(() => {
            reactFlowInstance.fitView({ padding: 0.25, duration: 400 });
          }, 0);
        }

        setExpandedNodes((prev) => ({ ...prev, [nodeId]: true }));
      } catch (err) {
        setExpandErrors((prev) => ({
          ...prev,
          [nodeId]: err.message || "Failed to expand lineage",
        }));
      } finally {
        setExpandingNodes((prev) => {
          const next = { ...prev };
          delete next[nodeId];
          return next;
        });
      }
    },
    [edges, expandedNodes, expandingNodes, nodes, reactFlowInstance, setEdges, setNodes, sourceType]
  );

  const onNodeClick = useCallback(
    async (event, node) => {
      // Toggle selection
      setSelectedNode(selectedNode === node.id ? null : node.id);
      
      // If it's a table node (not report node), optionally trigger lineage view
      if (node.id !== "report-node" && onTableClick) {
        const tableName = node.data?.sourceFullName || node.data?.tableName || node.data?.label;
        onTableClick(tableName);
      }

      if (node.id !== "report-node") {
        const sourceFullName = node.data?.sourceFullName || node.data?.tableName || node.data?.label;
        await expandTableLineage(node.id, sourceFullName);
      }
    },
    [expandTableLineage, onTableClick, selectedNode]
  );

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Update nodes with selection state
  const nodesWithSelection = useMemo(() => {
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        selected: selectedNode === node.id,
        expanded: !!expandedNodes[node.id],
        isLoading: !!expandingNodes[node.id],
        expandError: expandErrors[node.id] || null,
        onExpandNode:
          node.id !== "report-node"
            ? () =>
                expandTableLineage(
                  node.id,
                  node.data?.sourceFullName || node.data?.tableName || node.data?.label
                )
            : null,
      },
      selected: selectedNode === node.id,
    }));
  }, [nodes, selectedNode, expandedNodes, expandingNodes, expandErrors, expandTableLineage]);

  if (!reportName) {
    return (
      <div className="bg-gray-50 rounded-lg min-h-56 flex items-center justify-center text-gray-400 text-sm">
        Report name not available
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-lg min-h-56 flex items-center justify-center text-gray-500 text-sm">
        Loading report lineage...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-lg min-h-56 flex items-center justify-center text-red-600 text-sm px-4 text-center">
        Failed to load report lineage: {error}
      </div>
    );
  }

  if (!lineageData || !Array.isArray(lineageData.upstreams) || lineageData.upstreams.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg min-h-56 flex items-center justify-center text-gray-500 text-sm">
        No report lineage found
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <div style={{ width: "100%", height: "80vh", minHeight: "30vh", position: "relative" }}>
        <ReactFlow
          nodes={nodesWithSelection}
          edges={edges}
          onInit={setReactFlowInstance}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.2}
          maxZoom={4}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        >
          {/* Top-right controls with zoom, fit view, and interactive mode */}
          <Controls
            position="top-right"
            showZoom={true}
            showFitView={true}
            showInteractive={true}
          />

          {/* Bottom-left minimap for overview navigation */}
          <MiniMap
            position="bottom-left"
            width={200}
            height={150}
            style={{
              backgroundColor: "#f3f4f6",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
          />

          {/* Grid background for better spatial reference */}
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            color="#d1d5db"
          />
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
};

export default ReportLIneage;
